import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { IMAGES, LINKS } from "../data";
import { useBooking } from "../BookingContext";
import logoWhite from "../assets/logo-white.png";

const STAGES = ["CORNEA", "LENS", "RETINA", "THE LAYERS"];

const HudNav = ({ active }) => (
  <div
    className="absolute left-5 md:left-12 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-5 border-l border-white/15 pl-4 md:pl-5 pointer-events-none"
    data-testid="eye-journey-hud"
  >
    {STAGES.map((s, i) => (
      <div key={s} className="flex items-center gap-3">
        <span
          className={`hud-line text-[10px] md:text-xs uppercase font-semibold ${
            active === i
              ? "text-[#F59E0B] tracking-[0.32em] opacity-100"
              : "text-white/40 tracking-[0.2em] opacity-60"
          }`}
        >
          {s}
        </span>
        {active === i && <span className="h-px w-6 bg-[#F59E0B]" />}
      </div>
    ))}
  </div>
);

export default function EyeJourney() {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const booking = useBooking();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const s = p < 0.3 ? 0 : p < 0.55 ? 1 : p < 0.82 ? 2 : 3;
    setActive(s);
  });

  // Iris
  const irisScale = useTransform(scrollYProgress, [0, 0.35], [1, 6]);
  const irisOpacity = useTransform(scrollYProgress, [0, 0.28, 0.37], [1, 1, 0]);
  // Lens
  const lensOpacity = useTransform(scrollYProgress, [0.28, 0.42, 0.62, 0.7], [0, 1, 1, 0]);
  const lensScale = useTransform(scrollYProgress, [0.28, 0.7], [1.1, 3.4]);
  // Retina
  const retinaOpacity = useTransform(scrollYProgress, [0.6, 0.72, 0.9, 1], [0, 1, 1, 0.45]);
  const retinaScale = useTransform(scrollYProgress, [0.6, 1], [1.3, 1]);
  // OCT preview
  const octOpacity = useTransform(scrollYProgress, [0.86, 0.97], [0, 0.85]);
  const octScale = useTransform(scrollYProgress, [0.86, 1], [1.12, 1]);

  // Text pins
  const heroOpacity = useTransform(scrollYProgress, [0, 0.06, 0.13], [1, 1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 0.13], [0, -60]);
  const optosOpacity = useTransform(scrollYProgress, [0.36, 0.43, 0.52, 0.58], [0, 1, 1, 0]);
  const retTextOpacity = useTransform(scrollYProgress, [0.66, 0.73, 0.8, 0.85], [0, 1, 1, 0]);
  const layerTextOpacity = useTransform(scrollYProgress, [0.88, 0.95], [0, 1]);

  const cueOpacity = useTransform(scrollYProgress, [0, 0.05], [1, 0]);

  return (
    <section ref={ref} className="relative h-[560vh] bg-[#08080b]" data-testid="eye-journey">
      <div className="sticky top-0 h-screen w-full overflow-hidden grain">
        {/* Layer stack */}
        <motion.img
          src={IMAGES.iris}
          alt="Human iris in macro"
          style={{ scale: irisScale, opacity: irisOpacity }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <motion.img
          src={IMAGES.lens}
          alt="The crystalline lens"
          style={{ scale: lensScale, opacity: lensOpacity }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <motion.img
          src={IMAGES.retina}
          alt="The retina illuminated"
          style={{ scale: retinaScale, opacity: retinaOpacity }}
          className="absolute inset-0 h-full w-full object-cover edge-blend"
        />
        <motion.img
          src={IMAGES.octLayers}
          alt="Retinal layers separating"
          style={{ scale: octScale, opacity: octOpacity }}
          className="absolute inset-0 h-full w-full object-cover edge-blend"
        />

        {/* subtle vignette for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/50 z-[4] pointer-events-none" />

        <HudNav active={active} />

        {/* HERO pin */}
        <motion.div
          style={{ opacity: heroOpacity, y: heroY }}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
        >
          <div className="caustic" />
          <h1 className="sr-only">West Springs Optometry</h1>
          <img
            src={logoWhite}
            alt="West Springs Optometry"
            data-testid="hero-logo"
            className="relative z-[3] w-[80vw] max-w-[300px] sm:max-w-[380px] lg:max-w-[520px] h-auto drop-shadow-[0_2px_24px_rgba(0,0,0,0.45)]"
          />
          <p className="relative z-[3] mt-7 text-white/85 text-base md:text-lg max-w-md font-light">
            Accepting new patients. Walk-ins welcome. Same-day appointments.
          </p>
          <a
            href={LINKS.bookOnlineHref}
            onClick={(e) => { e.preventDefault(); booking.open(LINKS.bookOnline, "Book Your Appointment"); }}
            data-testid="book-online-hero-btn"
            className="relative z-[3] mt-9 inline-flex items-center gap-2 rounded-full bg-[#F59E0B] px-8 py-3.5 text-sm font-medium text-[#1A1D20] hover:bg-white transition-colors duration-300 pointer-events-auto cursor-pointer"
          >
            Book Online
          </a>
          <motion.div style={{ opacity: cueOpacity }} className="absolute bottom-10 z-[3] flex flex-col items-center text-white/70">
            <span className="text-[10px] uppercase tracking-[0.3em] mb-2">Scroll into the eye</span>
            <ChevronDown className="scroll-pulse" size={20} />
          </motion.div>
        </motion.div>

        {/* OPTOS / descent pin */}
        <motion.div
          style={{ opacity: optosOpacity }}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
        >
          <p className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#F59E0B] mb-5">The Optos California</p>
          <h2 className="font-display font-light text-white text-4xl md:text-6xl leading-tight max-w-3xl">
            One scan. 200 degrees.<br />82% of your retina in a single image.
          </h2>
          <p className="mt-6 max-w-xl text-white/80 font-light text-sm md:text-base">
            Ultra-widefield imaging captures a detailed digital photograph of the retina — the
            light-sensitive tissue at the back of your eye — in a single pass.
          </p>
        </motion.div>

        {/* RETINA pin */}
        <motion.div
          style={{ opacity: retTextOpacity }}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
        >
          <h2 className="font-display font-light text-white text-4xl md:text-6xl leading-tight max-w-3xl">
            This is your retina.
          </h2>
          <p className="mt-5 max-w-lg text-white/80 font-light text-sm md:text-base">
            River-delta blood vessels branching across an amber field — the exact landscape our
            cameras photograph, every exam.
          </p>
        </motion.div>

        {/* LAYERS pin */}
        <motion.div
          style={{ opacity: layerTextOpacity }}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
        >
          <p className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#F59E0B] mb-5">Optical Coherence Tomography</p>
          <h2 className="font-display font-light text-white text-4xl md:text-6xl leading-tight max-w-3xl">
            Then it opens<br />into its layers.
          </h2>
        </motion.div>
      </div>
    </section>
  );
}
